/**
 * Ticket Lifecycle v2 — Slice 4A
 *
 * FOH Custody Queue
 *
 * Shows tickets that need front-of-house attention: anything currently
 * sitting in `pending_advisor_accept`, `with_advisor`, or
 * `pending_tech_accept`. Displays the per-ticket dwell time in the
 * current custody state and offers the appropriate single forward
 * action (Accept Ticket / Kick Back to Tech / Accept Back) per row.
 *
 * Reuses:
 *   - `useTicketCustodyMutation` (transitions via the slice-2 edge fn)
 *   - `primaryCustodyAction` (single-authority status→action map)
 *   - `TicketCustodyBadge` (shared chip)
 *
 * Display-only with respect to scheduling: this component never reads
 * or writes scheduling, timer, parking, rollover, or bay fields. The
 * action buttons only invoke `_apply_custody_transition`.
 *
 * Role visibility (spec §6.1): the component itself is role-agnostic.
 * RLS scopes reads to shop members; the edge function role-gates writes
 * to `OWNER | MANAGER | ORG_ADMIN | SERVICE_WRITER | TECHNICIAN`. A user
 * outside that set will receive a `403` toast on action click — defense
 * in depth.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNowStrict, isToday, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowRightCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useTicketCustodyMutation,
  primaryCustodyAction,
  type TicketCustodyRow,
} from '@/hooks/tickets/useTicketCustody';
import { usePermissions } from '@/hooks/usePermissions';
import { TicketCustodyBadge } from '@/components/tickets/TicketCustodyBadge';
import { useCustodyActorNames } from '@/hooks/tickets/useCustodyActorNames';
import { isInFohActiveQueue } from '@/lib/tickets/lifecycle/fohActiveQueue';
import {
  HANDOFF_EVENT_KINDS,
  custodyActorDisplay,
  latestMeaningfulHandoff,
  toCustodyHistoryEntries,
  type CustodyHistoryEntry,
  type CustodyHistoryRow,
} from '@/lib/tickets/lifecycle/custodyHistory';
import type { CustodyStatus } from '@/lib/tickets/lifecycle/types';
import {
  getCustodyDwellAgingLevel,
  type CustodyAgingLevel,
} from '@/lib/tickets/lifecycle/custodyAging';
import {
  CustodyHandoffReasonDialog,
  type CustodyHandoffReasonDialogResult,
} from '@/components/tickets/CustodyHandoffReasonDialog';
import {
  buildCustodyHandoffMetadata,
  getCustodyHandoffReasonLabel,
} from '@/lib/tickets/lifecycle/custodyHandoffReasons';

interface QueueRowVehicle {
  year: number | null;
  make: string | null;
  model: string | null;
}

interface QueueRowTicket {
  id: string;
  simple_ticket_number: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  status: string | null;
  vehicle: QueueRowVehicle | null;
}

export interface FohQueueRow extends TicketCustodyRow {
  ticket: QueueRowTicket | null;
}

/**
 * The three custody states that warrant FOH attention. Pure constant
 * so the test suite and any future filtering helpers reuse the same
 * source-of-truth tuple.
 */
export const FOH_QUEUE_STATUSES = [
  'pending_advisor_accept',
  'with_advisor',
  'pending_tech_accept',
] as const satisfies ReadonlyArray<CustodyStatus>;

export function isFohQueueStatus(s: CustodyStatus): boolean {
  return (FOH_QUEUE_STATUSES as readonly string[]).includes(s);
}

/**
 * Stable display order: pending_advisor_accept (needs FOH to accept) →
 * with_advisor (FOH actively working) → pending_tech_accept (FOH has
 * kicked back, waiting for tech). Within a status, ordered by
 * `entered_at` ascending (oldest first = most urgent).
 */
const STATUS_ORDER: Record<CustodyStatus, number> = {
  pending_advisor_accept: 0,
  with_advisor: 1,
  pending_tech_accept: 2,
  with_tech: 99,
  // Oct 20: terminal (picked up) — never in the queue; ordered last if a stale row is ever passed in.
  released_to_customer: 100,
};

export function sortFohQueueRows<T extends { custody_status: CustodyStatus; entered_at: string }>(
  rows: ReadonlyArray<T>,
): T[] {
  return [...rows].sort((a, b) => {
    const da = STATUS_ORDER[a.custody_status] ?? 99;
    const db = STATUS_ORDER[b.custody_status] ?? 99;
    if (da !== db) return da - db;
    return a.entered_at.localeCompare(b.entered_at);
  });
}

function formatVehicle(v: QueueRowVehicle | null | undefined): string {
  if (!v) return '—';
  const parts = [v.year, v.make, v.model].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
}

function formatTicketNumber(t: QueueRowTicket | null | undefined): string {
  if (!t) return '—';
  if (t.simple_ticket_number != null) {
    return `#TKT-${String(t.simple_ticket_number).padStart(4, '0')}`;
  }
  return `#${t.id.slice(0, 8)}`;
}

interface Props {
  shopId: string;
}

export function FohCustodyQueue({ shopId }: Props) {
  const navigate = useNavigate();
  const mutation = useTicketCustodyMutation();
  // Slice 4C: reference time for aging computation. Captured once per
  // render so all rows compare against the same wall-clock. Not pure;
  // the underlying helper is pure when called with explicit refTime.
  const referenceTime = new Date().toISOString();
  const { canReassignAdvisor } = usePermissions();

  // Batch 1 (FOH timer handoff): reason picker state for the
  // kick-back-to-tech action. Accept actions still fire immediately.
  const [pendingKickBack, setPendingKickBack] = useState<{
    ticketId: string;
  } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['foh-custody-queue', shopId],
    enabled: !!shopId,
    retry: false,
    queryFn: async (): Promise<FohQueueRow[]> => {
      const { data, error } = await (supabase as any)
        .from('ticket_custody')
        .select(
          `
          ticket_id, shop_id, custody_owner, custody_status,
          holder_user_id, entered_at, entered_by, last_event_id,
          created_at, updated_at,
          ticket:tickets!ticket_id (
            id, simple_ticket_number, customer_name, customer_phone, status,
            picked_up_at, completed_at, deleted_at, archived_at,
            vehicle:vehicles ( year, make, model )
          )
        `,
        )
        .eq('shop_id', shopId)
        .in('custody_status', FOH_QUEUE_STATUSES)
        .order('entered_at', { ascending: true });
      if (error) {
        console.error('[foh-queue] failed to read ticket_custody', error);
        throw error;
      }
      return (data ?? []) as FohQueueRow[];
    },
  });

  // The ACTIVE queue: a vehicle already picked up (complete or not) has
  // nothing left for the front office; its custody row and history stay.
  const rows = useMemo(
    () => sortFohQueueRows((data ?? []).filter((r) => isInFohActiveQueue(r.ticket as never))),
    [data],
  );

  // The handoff that explains each row: its latest MEANINGFUL handoff (a
  // kick), batched in one query. NOT `last_event_id`: after foh_accept that
  // points at the accept row, which carries no reason, and the queue used to
  // fall back to "No reason provided" for every accepted ticket. Keyed on the
  // rows' last_event_ids too, so any new custody event refetches it.
  const ticketIds = useMemo(() => rows.map((r) => r.ticket_id), [rows]);
  const lastEventKey = useMemo(() => rows.map((r) => r.last_event_id ?? '').join('|'), [rows]);

  const { data: handoffByTicket } = useQuery({
    queryKey: ['foh-custody-queue-reasons', shopId, ticketIds.join('|'), lastEventKey],
    enabled: !!shopId && ticketIds.length > 0,
    retry: false,
    queryFn: async (): Promise<Record<string, CustodyHistoryEntry>> => {
      const { data, error } = await (supabase as any)
        .from('ticket_custody_events')
        .select('id, ticket_id, event_kind, from_owner, to_owner, actor_id, actor_role, reason, metadata, created_at')
        .in('ticket_id', ticketIds)
        .in('event_kind', HANDOFF_EVENT_KINDS as unknown as string[])
        .order('created_at', { ascending: false });
      if (error) {
        // Reason display is non-critical — log and degrade gracefully.
        console.warn('[foh-queue] failed to read handoff reasons', error);
        return {};
      }
      const byTicket = new Map<string, CustodyHistoryRow[]>();
      for (const row of (data ?? []) as Array<CustodyHistoryRow & { ticket_id: string }>) {
        const list = byTicket.get(row.ticket_id) ?? [];
        list.push(row);
        byTicket.set(row.ticket_id, list);
      }
      const map: Record<string, CustodyHistoryEntry> = {};
      for (const [ticketId, list] of byTicket) {
        const latest = latestMeaningfulHandoff(toCustodyHistoryEntries(list));
        if (latest) map[ticketId] = latest;
      }
      return map;
    },
  });
  const { data: actorNames } = useCustodyActorNames(
    Object.values(handoffByTicket ?? {}).map((h) => h.actorId),
  );

  const handleKickBackConfirm = (
    ticketId: string,
    result: CustodyHandoffReasonDialogResult,
  ) => {
    const metadata = buildCustodyHandoffMetadata({
      trigger: 'manual',
      reason: result.reason,
      reasonLabel: result.reasonLabel,
      notes: result.notes,
      sourceComponent: 'FohCustodyQueue',
    });
    mutation
      .mutateAsync({
        ticketId,
        eventKind: 'kick_to_boh',
        reason: result.reason,
        metadata,
      })
      .then(() => {
        setPendingKickBack(null);
      })
      .catch(() => {
        /* mutation hook surfaces toast */
      });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>FOH Active Queue</CardTitle>
          <CardDescription>Tickets currently needing front-of-house attention</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading queue…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>FOH Active Queue</CardTitle>
          <CardDescription className="text-destructive">
            Failed to load custody queue. The slice-2 migration may not be applied to this Supabase project.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            {(error as Error)?.message || 'Unknown error.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>FOH Active Queue</CardTitle>
        <CardDescription>
          Tickets currently needing front-of-house attention
          {rows.length > 0 ? ` (${rows.length})` : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing in the FOH queue right now.
          </p>
        ) : (
          <div className="space-y-2" data-testid="foh-queue-rows">
            {rows.map((row) => {
              // CORE-AK: FOH acceptance is offered only to people who act as front office.
              const action = primaryCustodyAction(row.custody_status, { actsAsFrontOffice: !!canReassignAdvisor });
              const dwellMs = Date.parse(row.entered_at);
              const dwell = Number.isFinite(dwellMs)
                ? formatDistanceToNowStrict(new Date(dwellMs), { addSuffix: false })
                : '—';
              // CUS-1: dwell warnings now grade every queue state via
              // per-state thresholds — pending_advisor_accept and
              // pending_tech_accept (BOH-return) at 5/10 min, with_advisor
              // (advisor-active) at the longer 20/40-min dwell. with_tech
              // remains ungraded. (Previously only pending_advisor_accept
              // was graded; the rest silently stalled.)
              const agingLevel: CustodyAgingLevel = getCustodyDwellAgingLevel(
                row.custody_status,
                row.entered_at,
                referenceTime,
              );
              const isWarning = agingLevel === 'warning';
              const isUrgent = agingLevel === 'urgent';
              return (
                <div
                  key={row.ticket_id}
                  data-testid={`foh-queue-row-${row.ticket_id}`}
                  data-aging-level={agingLevel}
                  className={cn(
                    // Slice 4C.1: `flex-wrap` keeps wide content inside the
                    // row card on medium-width viewports; previously the
                    // dwell-time text + Accept button overflowed past the
                    // bordered background.
                    'flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-4 p-3 rounded-lg border-2 bg-card',
                    isUrgent && 'border-destructive bg-destructive/5',
                    isWarning && 'border-amber-500 bg-amber-500/5',
                    !isUrgent && !isWarning && 'border-border',
                  )}
                >
                  <div className="font-mono text-sm min-w-[110px] flex items-center gap-1.5">
                    {(isWarning || isUrgent) && (
                      <AlertTriangle
                        className={cn(
                          'h-4 w-4 shrink-0',
                          isUrgent ? 'text-destructive' : 'text-amber-600',
                        )}
                        aria-label={isUrgent ? 'Urgent — overdue' : 'Aging — waiting for advisor'}
                      />
                    )}
                    {formatTicketNumber(row.ticket)}
                  </div>
                  <div className="text-sm min-w-[160px]">
                    <div>{row.ticket?.customer_name || '—'}</div>
                    {row.ticket?.customer_phone && (
                      <div className="text-xs text-muted-foreground">
                        {row.ticket.customer_phone}
                      </div>
                    )}
                  </div>
                  <div className="text-sm min-w-[180px] text-muted-foreground">
                    {formatVehicle(row.ticket?.vehicle ?? null)}
                  </div>
                  <div className="min-w-[160px]">
                    <TicketCustodyBadge custody={row} />
                  </div>
                  <div
                    className={cn(
                      'text-xs min-w-[140px]',
                      isUrgent
                        ? 'text-destructive font-semibold'
                        : isWarning
                          ? 'text-amber-700 dark:text-amber-400 font-medium'
                          : 'text-muted-foreground',
                    )}
                  >
                    {isUrgent
                      ? `URGENT — waiting ${dwell}`
                      : isWarning
                        ? `waiting ${dwell}`
                        : `in state ${dwell}`}
                  </div>
                  {/* The handoff that explains this row: its latest kick
                      (reason, who, when, note) — it survives the accept.
                      Legacy kicks with no reason render "No reason
                      provided" via the shared helper. */}
                  {(() => {
                    const handoff = handoffByTicket?.[row.ticket_id];
                    if (!handoff) return null;
                    const label = handoff.reason ?? getCustodyHandoffReasonLabel(null);
                    const who = custodyActorDisplay(handoff.actorId, handoff.actorRole, actorNames);
                    const at = Number.isFinite(Date.parse(handoff.createdAt))
                      ? (isToday(parseISO(handoff.createdAt))
                        ? format(parseISO(handoff.createdAt), 'h:mm a')
                        : format(parseISO(handoff.createdAt), 'MMM d, h:mm a'))
                      : '';
                    return (
                      // Reason cell: allow growth into available row
                      // space (flex-1) and wrap long reasons rather
                      // than clip. The min-w floor keeps short reasons
                      // ("Parts arrived") readable on narrow viewports;
                      // break-words handles run-on text without forcing
                      // the row to overflow horizontally.
                      <div
                        className="text-xs text-muted-foreground flex-1 min-w-[180px] break-words"
                        data-testid={`foh-queue-reason-${row.ticket_id}`}
                      >
                        <span className="font-medium text-foreground/80">
                          Reason:
                        </span>{' '}
                        <span className="whitespace-normal">{label}</span>
                        <div data-testid={`foh-queue-handoff-who-${row.ticket_id}`}>
                          {who.primary}{at ? ` · ${at}` : ''}
                        </div>
                        {handoff.notes && (
                          <div
                            className="italic text-foreground/80 whitespace-pre-wrap"
                            data-testid={`foh-queue-notes-${row.ticket_id}`}
                          >
                            “{handoff.notes}”
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="md:ml-auto">
                    {action && (
                      <Button
                        size="sm"
                        variant={isUrgent ? 'destructive' : 'default'}
                        onClick={() => {
                          const ticketId = row.ticket_id;
                          // Batch 1: kick-back-to-tech now opens the
                          // structured reason picker. Accept actions
                          // (foh_accept, boh_accept) fire immediately
                          // — they are not handoffs.
                          if (action.kind === 'kick_to_boh') {
                            setPendingKickBack({ ticketId });
                            return;
                          }
                          const isAcceptFromQueue = action.kind === 'foh_accept';
                          mutation
                            .mutateAsync({
                              ticketId,
                              eventKind: action.kind,
                              reason: null,
                            })
                            .then(() => {
                              if (isAcceptFromQueue) {
                                navigate(
                                  `/?tab=dayboard&highlight=${encodeURIComponent(ticketId)}&openDetails=true`,
                                );
                              }
                            })
                            .catch(() => {
                              /* mutation hook surfaces toast */
                            });
                        }}
                        disabled={mutation.isPending}
                        data-testid={`foh-queue-action-${row.ticket_id}`}
                        className="gap-1"
                      >
                        <ArrowRightCircle className="h-3 w-3" />
                        {action.label}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Batch 1: shared kick-back reason picker. Mounted lazily — the
          dialog body only renders while a kick-back is in flight. */}
      {pendingKickBack && (
        <CustodyHandoffReasonDialog
          open={!!pendingKickBack}
          onOpenChange={(open) => {
            if (!open) setPendingKickBack(null);
          }}
          trigger="manual"
          eventKind="kick_to_boh"
          isPending={mutation.isPending}
          onConfirm={(result) =>
            handleKickBackConfirm(pendingKickBack.ticketId, result)
          }
        />
      )}
    </Card>
  );
}
